import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const countryCodes = {
  Australia: 'au',
  Bangladesh: 'bd',
  Cambodia: 'kh',
  Chile: 'cl',
  China: 'cn',
  Croatia: 'hr',
  Egypt: 'eg',
  EU: 'eu',
  France: 'fr',
  Iceland: 'is',
  India: 'in',
  Indonesia: 'id',
  Ireland: 'ie',
  Japan: 'jp',
  Kenya: 'ke',
  Lao: 'la',
  Luxembourg: 'lu',
  Malaysia: 'my',
  Myanmar: 'mm',
  Netherlands: 'nl',
  Nepal: 'np',
  Philippines: 'ph',
  'Saint Kitts and Nevis': 'kn',
  'Sao Tome and Principe': 'st',
  Singapore: 'sg',
  Spain: 'es',
  Sweden: 'se',
  Thailand: 'th',
  UK: 'uk',
  Vietnam: 'vn',
};

function formatColumnHeading(column) {
  if (column === 'Country') {
    return 'Country/region';
  }

  if (column.toLowerCase() === 'energy efficiency') {
    return (
      <>
        Energy
        <br />
        {'effi\u00adciency'}
      </>
    );
  }

  return column === 'Electrification' ? 'Electri\u00adfication' : column;
}

const ContentSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 28px 8px 36px;
  color: #242424;

  @media (min-width: 620px) {
    padding-right: 24px;
    padding-left: 24px;
  }
`;

const PolicyTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border: 1px solid #d8d8d8;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 2px;
  background: #fff;
  overflow: hidden;

  th,
  td {
    padding: 10px 12px;
    border-right: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;
    vertical-align: middle;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: 0;
  }

  thead th {
    color: #fff;
    background: #243f6f;
    font-size: 0.8rem;
    line-height: 1.3em;
    overflow-wrap: normal;
    text-align: left;
  }

  thead th:first-child {
    width: 40%;
    border-top: 3px solid #e12827;
  }

  tbody th {
    min-width: 135px;
    color: #243f6f;
    background: #fafafa;
    font-weight: 700;
    text-align: left;
  }

  tbody td {
    width: 1%;
    text-align: center;
  }

  tbody tr:hover th,
  tbody tr:hover td {
    background: #fff8f2;
  }

  @media (max-width: 619px) {
    font-size: 0.85rem;

    th,
    td {
      padding: 9px 6px;
    }

    tbody th {
      min-width: 0;
    }
  }
`;

const PolicyButton = styled.button`
  display: inline-grid;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #d8d8d8;
  border-radius: 50%;
  place-items: center;
  color: #243f6f;
  background: #fff;
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: 700;

  &:hover {
    border-color: #f06f10;
    color: #e12827;
    background: #fff1e6;
  }

  &:focus-visible {
    outline: 3px solid #f06f10;
    outline-offset: 2px;
  }
`;

const CountryLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Flag = styled.img`
  display: block;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  object-fit: cover;
`;

const PolicyDialog = styled.dialog`
  position: relative;
  width: min(420px, calc(100vw - 32px));
  padding: 0;
  border: 1px solid #d8d8d8;
  border-left: 4px solid #e12827;
  border-radius: 2px;
  overflow: hidden;
  color: #242424;
  background: #fff;
  box-shadow: 0 10px 28px rgb(36 63 111 / 22%);
`;

const PolicyDialogContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  padding: 16px 18px 14px;
  color: inherit;
  line-height: 1.45;
  text-align: left;
`;

const PolicyEyebrow = styled.span`
  display: block;
  max-width: 80%;
  margin-bottom: 5px;
  color: #e12827;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
`;

const PolicyTitle = styled.strong`
  display: block;
  max-width: 80%;
  color: #243f6f;
  font-size: 1.05rem;
  line-height: 1.25;
  overflow-wrap: break-word;
`;

const PolicyText = styled.span`
  display: block;
  max-width: 90%;
  margin-top: 10px;
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: normal;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  min-width: 66px;
  padding: 6px 10px;
  border: 1px solid #243f6f;
  border-radius: 999px;
  color: #fff;
  background: #243f6f;
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;

  &:hover {
    border-color: #f06f10;
    background: #f06f10;
  }

  &:focus-visible {
    outline: 3px solid #f06f10;
    outline-offset: 2px;
  }
`;

export default function Content() {
  const policies = useSelector((state) => state.data.data);
  const [activePolicy, setActivePolicy] = useState(null);
  const [dialogTop, setDialogTop] = useState(null);
  const dialogRef = useRef(null);
  const columns = policies.length ? Object.keys(policies[0]) : [];

  useEffect(() => {
    if (!activePolicy) {
      return undefined;
    }

    const closePolicy = () => setActivePolicy(null);
    const listenerTimer = window.setTimeout(() => {
      document.addEventListener('click', closePolicy);
    }, 0);

    return () => {
      window.clearTimeout(listenerTimer);
      document.removeEventListener('click', closePolicy);
    };
  }, [activePolicy]);

  useLayoutEffect(() => {
    if (!activePolicy || !dialogRef.current) {
      return;
    }

    const { height } = dialogRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - activePolicy.top - 16;
    const nextTop =
      height > spaceBelow
        ? Math.max(16, activePolicy.top - height)
        : activePolicy.top;

    setDialogTop(nextTop);
  }, [activePolicy]);

  const renderPolicyCell = (policy, country, category) => {
    if (!policy) {
      return <td>&mdash;</td>;
    }

    return (
      <td>
        <PolicyButton
          type="button"
          aria-label={`View ${category} policy for ${country}`}
          onClick={(event) => {
            const { top } = event.currentTarget.getBoundingClientRect();
            setDialogTop(null);
            setActivePolicy({
              country,
              category,
              policy,
              top,
            });
          }}
        >
          ✓
        </PolicyButton>
      </td>
    );
  };

  return (
    <ContentSection>
      {policies.length ? (
        <PolicyTable>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column} scope="col">
                  {formatColumnHeading(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {policies.map((row) => (
              <tr key={row.Country}>
                <th scope="row">
                  <CountryLabel>
                    {countryCodes[row.Country] && (
                      <Flag
                        src={`images/${countryCodes[row.Country]}.svg`}
                        alt=""
                      />
                    )}
                    {row.Country}
                  </CountryLabel>
                </th>
                {columns
                  .slice(1)
                  .map((category) =>
                    renderPolicyCell(row[category], row.Country, category),
                  )}
              </tr>
            ))}
          </tbody>
        </PolicyTable>
      ) : (
        <p>Loading structural policies…</p>
      )}

      {activePolicy && (
        <PolicyDialog
          ref={dialogRef}
          open
          aria-labelledby="policy-dialog-title"
          style={{
            position: 'fixed',
            top: `${dialogTop ?? activePolicy.top}px`,
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            margin: 0,
            width: 'calc(100vw - 32px)',
            maxWidth: '420px',
            boxSizing: 'border-box',
            transform: 'translateX(-50%)',
          }}
        >
          <CloseButton
            type="button"
            aria-label={`Close policy details for ${activePolicy.country}`}
            onClick={() => {
              setActivePolicy(null);
              setDialogTop(null);
            }}
          >
            Close ×
          </CloseButton>
          <PolicyDialogContent>
            <PolicyEyebrow>Structural policy</PolicyEyebrow>
            <PolicyTitle id="policy-dialog-title">
              {activePolicy.country}: {activePolicy.category}
            </PolicyTitle>
            <PolicyText>{activePolicy.policy}</PolicyText>
          </PolicyDialogContent>
        </PolicyDialog>
      )}
    </ContentSection>
  );
}
